import React from "react";
import {
  Alert, Chip, CircularProgress, List, ListItemButton, ListItemText, Stack, Typography,
  ToggleButtonGroup, ToggleButton, Tooltip, Paper, Checkbox,
} from "@mui/material";
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

export default function Step2SelectGbif({
  dbParent,
  gbifSearchError,
  gbifSearchLoading,
  displayedGbifResults,
  gbifParentSelection,
  onToggleGbifParent,
  datasetMetaCache,
  gbifSortOrder,
  onChangeSortOrder,
  gbifFreshnessFilter,
  onChangeFreshnessFilter,
  getDatasetName,
  getFreshnessCategory,
  GBIF_BACKBONE_DATASET_KEY,
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Step 2: 親として扱うIDをGBIFから取得</Typography>
        <Typography>親分類群「<strong>{dbParent?.name_spell_valid}</strong>」に対応するGBIFレコードを選択してください。ここで選択したレコードを元に配下を検索します。</Typography>
        {dbParent?.gbif_usage_key && <Alert severity="info">既存のGBIF Usage Key: {dbParent.gbif_usage_key}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup value={gbifSortOrder} exclusive onChange={(_, v) => v && onChangeSortOrder?.(v)} size="small">
            <ToggleButton value="recommended">推奨順</ToggleButton>
            <ToggleButton value="freshness">鮮度順</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={gbifFreshnessFilter} exclusive onChange={(_, v) => v && onChangeFreshnessFilter?.(v)} size="small">
            <ToggleButton value="all">すべて</ToggleButton>
            <ToggleButton value="fresh">Fresh</ToggleButton>
            <ToggleButton value="stale">Stale</ToggleButton>
            <ToggleButton value="archived">Archived</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {gbifSearchError && <Alert severity="error">{gbifSearchError}</Alert>}
        {gbifSearchLoading ? (
          <Stack alignItems="center" py={4}><CircularProgress /></Stack>
        ) : (
          <List dense sx={{ maxHeight: 500, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {displayedGbifResults.map((r) => {
              const key = String(r.key);
              const isSelected = gbifParentSelection.ids.has(key);
              const meta = datasetMetaCache.get(r.datasetKey) || {};
              const datasetName = meta.title || getDatasetName(r);
              const freshness = getFreshnessCategory(r);
              const metaError = meta?.error;
              let freshnessChip = null;
              if (freshness !== 'unknown') {
                const chipProps = { fresh: { label: "Fresh", color: "success" }, stale: { label: "Stale", color: "warning" }, archived: { label: "Archived", color: "default" } }[freshness];
                freshnessChip = <Chip {...chipProps} size="small" variant="outlined" />;
              }
              const isBackbone = r.datasetKey === GBIF_BACKBONE_DATASET_KEY;
              return (
                <ListItemButton key={key} divider onClick={() => onToggleGbifParent?.(key)}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                    <Stack sx={{ pt: 0.5 }}>
                      <Checkbox edge="start" readOnly checked={isSelected} />
                    </Stack>
                    <ListItemText
                      primary={<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {isBackbone && <Tooltip title="GBIF Backbone"><VerifiedUserIcon fontSize="small" sx={{ color: 'success.main' }} /></Tooltip>}
                        <Typography variant="body2">{r.scientificName} {r.authorship || ''}</Typography>
                        <Chip size="small" label={r.rank} variant="outlined" />
                        <Chip size="small" label={r.taxonomicStatus} />
                      </Stack>}
                      secondary={<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="caption" color={metaError ? "error" : "text.secondary"}>
                          {datasetName}
                        </Typography>
                        {metaError && (
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label="Meta fetch failed"
                            sx={{ height: 18, "& .MuiChip-label": { px: 0.6, fontSize: "0.7rem" } }}
                          />
                        )}
                        {freshnessChip}
                      </Stack>}
                    />
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Stack>
    </Paper>
  );
}
